/**
 * Aurora Theme — core behaviors
 * Vanilla JS, no dependencies. Loaded with `defer` in layout/theme.liquid.
 */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /* ---------------------------------------------------------------------
   * Scroll-triggered fade-in animations
   * ------------------------------------------------------------------- */
  function initScrollAnimations() {
    var targets = document.querySelectorAll('[data-animate]');
    if (!targets.length) return;

    document.body.classList.add('animations-ready');

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------------
   * Mobile menu
   * ------------------------------------------------------------------- */
  function initMobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    var openBtn = document.querySelector('[data-mobile-menu-open]');
    var closeBtns = document.querySelectorAll('[data-mobile-menu-close]');
    if (!menu || !openBtn) return;

    var backdrop = document.createElement('div');
    backdrop.className = 'overlay-backdrop';
    document.body.appendChild(backdrop);

    function open() {
      menu.setAttribute('data-open', '');
      backdrop.setAttribute('data-open', '');
      menu.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      menu.removeAttribute('data-open');
      backdrop.removeAttribute('data-open');
      menu.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', open);
    closeBtns.forEach(function (btn) { btn.addEventListener('click', close); });
    backdrop.addEventListener('click', close);

    menu.querySelectorAll('[data-mobile-submenu-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var submenu = toggle.nextElementSibling;
        if (!submenu) return;
        submenu.classList.toggle('is-open');
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Search overlay
   * ------------------------------------------------------------------- */
  function initSearchOverlay() {
    var overlay = document.querySelector('[data-search-overlay]');
    var openBtn = document.querySelector('[data-search-open]');
    var closeBtn = document.querySelector('[data-search-close]');
    var input = document.querySelector('[data-search-input]');
    if (!overlay || !openBtn) return;

    function open() {
      overlay.setAttribute('data-open', '');
      overlay.setAttribute('aria-hidden', 'false');
      setTimeout(function () { if (input) input.focus(); }, 80);
    }
    function close() {
      overlay.removeAttribute('data-open');
      overlay.setAttribute('aria-hidden', 'true');
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* ---------------------------------------------------------------------
   * Announcement bar rotation
   * ------------------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;
    var items = bar.querySelectorAll('.announcement-bar__item');
    if (items.length < 2 || bar.dataset.autoplay !== 'true') return;

    var interval = parseInt(bar.dataset.interval, 10) || 5000;
    var index = 0;

    setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, interval);
  }

  /* ---------------------------------------------------------------------
   * Generic quantity selectors (used outside product/cart-specific scripts)
   * ------------------------------------------------------------------- */
  function initQuantitySelectors(root) {
    (root || document).querySelectorAll('.quantity-selector').forEach(function (selector) {
      var input = selector.querySelector('input[type="number"]');
      var decrease = selector.querySelector('[data-qty-decrease], [data-quick-view-qty-decrease], [data-cart-qty-decrease]');
      var increase = selector.querySelector('[data-qty-increase], [data-quick-view-qty-increase], [data-cart-qty-increase]');
      if (!input) return;

      if (decrease && !decrease.dataset.bound) {
        decrease.dataset.bound = 'true';
        decrease.addEventListener('click', function () {
          var value = Math.max((parseInt(input.value, 10) || 1) - 1, parseInt(input.min, 10) || 0);
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      if (increase && !increase.dataset.bound) {
        increase.dataset.bound = 'true';
        increase.addEventListener('click', function () {
          var value = (parseInt(input.value, 10) || 1) + 1;
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Main product: option/variant selection
   * ------------------------------------------------------------------- */
  function initProductForm() {
    var section = document.querySelector('[data-product-section]');
    if (!section) return;

    var product;
    try {
      product = JSON.parse(section.dataset.productJson);
    } catch (e) {
      return;
    }

    var form = section.querySelector('[data-product-form]');
    var variantInput = section.querySelector('[data-product-variant-id]');
    var priceWrap = section.querySelector('[data-product-price]');
    var addButton = section.querySelector('[data-add-to-cart]');
    var addLabel = section.querySelector('[data-add-to-cart-label]');
    var optionGroups = section.querySelectorAll('[data-product-option]');

    var purchaseOptions = section.querySelector('[data-purchase-options]');
    var purchaseTabs = section.querySelectorAll('[data-purchase-tab]');
    var purchasePanels = section.querySelectorAll('[data-purchase-panel]');
    var sellingPlanInput = section.querySelector('[data-selling-plan-input]');
    var sellingPlanRadios = section.querySelectorAll('[data-selling-plan-radio]');
    var purchaseMode = purchaseOptions ? 'subscription' : null;

    var selectedOptions = product.variants.length
      ? (product.variants.find(function (v) { return v.available; }) || product.variants[0]).options.slice()
      : [];

    // Seed selectedOptions from the currently marked swatches.
    optionGroups.forEach(function (group) {
      var position = parseInt(group.dataset.optionPosition, 10) - 1;
      var selected = group.querySelector('.option-swatch.is-selected');
      if (selected) selectedOptions[position] = selected.dataset.optionValue;
    });

    function findVariant(options) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (opt, i) { return opt === options[i]; });
      });
    }

    function formatMoney(cents) {
      return (cents / 100).toLocaleString(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD'
      });
    }

    function renderPriceValue(price, compareAt) {
      if (!priceWrap) return;
      var onSale = compareAt > price;
      var discountPercent = onSale ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
      priceWrap.innerHTML =
        '<span class="price' + (onSale ? ' price--sale' : '') + '">' +
        (onSale
          ? '<span class="price__sale">' + formatMoney(price) + '</span>' +
            '<span class="price__compare">' + formatMoney(compareAt) + '</span>' +
            '<span class="price__discount">-' + discountPercent + '%</span>'
          : '<span class="price__regular">' + formatMoney(price) + '</span>') +
        '</span>';
    }

    function computePlanPrice(basePrice, card) {
      if (!card) return basePrice;
      var type = card.dataset.adjustmentType;
      var value = parseFloat(card.dataset.adjustmentValue) || 0;
      if (type === 'percentage') return Math.round(basePrice - (basePrice * value) / 100);
      if (type === 'fixed_amount') return Math.round(basePrice - value * 100);
      if (type === 'price') return Math.round(value * 100);
      return basePrice;
    }

    function getCheckedPlanCard() {
      var checked = section.querySelector('[data-selling-plan-radio]:checked');
      return checked ? checked.closest('[data-selling-plan-card]') : null;
    }

    function refreshPrice(variant) {
      if (!variant) return;
      if (purchaseMode === 'subscription') {
        var card = getCheckedPlanCard();
        var planPrice = computePlanPrice(variant.price, card);
        renderPriceValue(planPrice, variant.price);
      } else {
        renderPriceValue(variant.price, variant.compare_at_price);
      }
    }

    function setPurchaseMode(mode) {
      purchaseMode = mode;
      purchaseTabs.forEach(function (tab) {
        var isActive = tab.dataset.purchaseTab === mode;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      purchasePanels.forEach(function (panel) {
        panel.hidden = panel.dataset.purchasePanel !== mode;
      });
      if (sellingPlanInput) {
        if (mode === 'subscription') {
          var card = getCheckedPlanCard();
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = card ? card.dataset.planId : '';
        } else {
          sellingPlanInput.disabled = true;
        }
      }
    }

    function initPurchaseOptions() {
      if (!purchaseOptions) return;
      purchaseTabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          setPurchaseMode(tab.dataset.purchaseTab);
          refreshPrice(findVariant(selectedOptions));
        });
      });
      sellingPlanRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          section.querySelectorAll('[data-selling-plan-card]').forEach(function (card) {
            card.classList.toggle('is-selected', card.querySelector('[data-selling-plan-radio]') === radio);
          });
          if (sellingPlanInput) sellingPlanInput.value = radio.value;
          refreshPrice(findVariant(selectedOptions));
        });
      });
      setPurchaseMode('subscription');
    }

    function updateUI(options) {
      var variant = findVariant(options);
      optionGroups.forEach(function (group) {
        var position = parseInt(group.dataset.optionPosition, 10) - 1;
        group.querySelectorAll('.option-swatch').forEach(function (swatch) {
          swatch.classList.toggle('is-selected', swatch.dataset.optionValue === options[position]);
        });
        var selectedLabel = group.querySelector('[data-option-selected]');
        if (selectedLabel) selectedLabel.textContent = options[position];
      });

      if (variant) {
        if (variantInput) variantInput.value = variant.id;
        refreshPrice(variant);
        if (addButton) {
          addButton.disabled = !variant.available;
          if (addLabel) addLabel.textContent = variant.available ? addLabel.dataset.addLabel || addButton.dataset.addLabel || 'Agregar al carrito' : 'Agotado';
        }
      } else if (addButton) {
        addButton.disabled = true;
      }
    }

    initPurchaseOptions();
    if (purchaseOptions) refreshPrice(findVariant(selectedOptions));

    optionGroups.forEach(function (group) {
      var position = parseInt(group.dataset.optionPosition, 10) - 1;
      group.querySelectorAll('.option-swatch').forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          selectedOptions[position] = swatch.dataset.optionValue;
          updateUI(selectedOptions);
        });
      });
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!addButton || addButton.disabled) return;
        var formData = new FormData(form);
        addButton.classList.add('is-loading');
        fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            document.dispatchEvent(new CustomEvent('cart:open'));
          })
          .catch(function () {})
          .finally(function () { addButton.classList.remove('is-loading'); });
      });
    }
  }

  function initProductGallery(root) {
    var scope = root || document;
    var galleries = scope.querySelectorAll('[data-product-gallery]');
    galleries.forEach(function (gallery) {
      var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      var slides = gallery.querySelectorAll('[data-gallery-slide]');
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var mediaId = thumb.dataset.mediaId;
          thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
          slides.forEach(function (s) { s.classList.toggle('is-active', s.dataset.mediaId === mediaId); });
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initScrollAnimations();
    initMobileMenu();
    initSearchOverlay();
    initAnnouncementBar();
    initQuantitySelectors();
    initProductForm();
    initProductGallery();
  });

  // Re-bind quantity selectors whenever new markup is injected (e.g. quick view, cart drawer refresh).
  document.addEventListener('theme:content-updated', function (e) {
    initQuantitySelectors(e.detail && e.detail.root);
    initProductGallery(e.detail && e.detail.root);
  });
})();
